/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module ItemsModule
 */

import {
    Component,
    Type,
} from '@angular/core';

import {
    IAvailabilityZone,
    IDatastoreInfo,
    INsxtCluster,
    INsxtTransportnode,
    IVcenterSharedDatastore,
} from 'generated-types';

import { L10nService } from '@vmw/ngx-vip';
import { AvailabilityZone } from 'object-types';
import { StringService } from 'string-service';
import { TWindowElement } from 'ajs/modules/data-model/data-model.types';
import { ObjectTypeItem } from 'ajs/modules/data-model/factories/object-type-item.factory';

import {
    withEditChildMessageItemMixin,
} from 'ajs/modules/data-model/mixins/with-edit-child-message-item.mixin';

import {
    withFullModalMixin,
} from 'ajs/js/utilities/mixins/with-full-modal.mixin';

import {
    RepeatedMessageItem,
} from 'ajs/modules/data-model/factories/repeated-message-item.factory';

import type { Item } from 'ajs/modules/data-model/factories/item.factory';
import { AZClusterConfigItem } from 'message-items/az-cluster-config.item.factory';
import { AZDatastoreConfigItem } from 'message-items/az-datastore-config.item.factory';
import { AZHostConfigItem } from 'message-items/az-host-config.item.factory';
import { AZVSphereZoneConfigItem } from 'message-items/az-vsphere-zone-config.item.factory';
import type { ClusterItem } from './cluster.item.factory';
import * as l10n from './availability-zone.l10n';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * Type of items loaded by Host collection.
 */
export interface IHostItem {
    name: string;
    tenant_ref: string;
    url: string;
    uuid: string;
    managed_object_id?: string;
}

/**
 * Partial AvailabilityZone config.
 */
type TAvailabilityZoneConfigPartial = Omit<
IAvailabilityZone,
'az_clusters' | 'az_datastores' | 'az_hosts' | 'vsphere_zones'
>;

/**
 * AvailabilityZone Config type.
 */
interface IAvailabilityZoneConfig extends TAvailabilityZoneConfigPartial {
    az_clusters: RepeatedMessageItem<AZClusterConfigItem>;
    az_datastores: RepeatedMessageItem<AZDatastoreConfigItem>;
    az_hosts: RepeatedMessageItem<AZHostConfigItem>;
    vsphere_zones?: RepeatedMessageItem<AZVSphereZoneConfigItem>;
}

/**
 * @description Availability Zone Item.
 * @author Sarthak Kapoor
 */
export class AvailabilityZoneItem extends
    withEditChildMessageItemMixin(withFullModalMixin(ObjectTypeItem)) {
    protected static ngDependencies = [
        'l10nService',
        'stringService',
    ];

    public getConfig: () => IAvailabilityZoneConfig;

    /**
     * Hash of NSX clusters fetched. Array of clusters will be mapped to the vcenters they belong to.
     */
    public nsxClusterObjects: Record<string, Record<string, INsxtCluster>>;

    /**
     * Hash of NSX datastores fetched. Array of datastores will be mapped to the vcenters they belong to.
     */
    public nsxDatastoreObjects: Record<string, Record<string, IVcenterSharedDatastore>>;

    /**
     * Hash of NSX hosts fetched. Array of hosts will be mapped to the vcenters they belong to.
     */
    public nsxHostObjects: Record<string, Record<string, INsxtTransportnode>>;

    /**
     * List of Vcenter Cluster items.
     */
    public vcenterClusterObjects: ClusterItem[];

    /**
     * Hash of Vcenter datastores. Each vcenter datastore will be mapped to it's managed_object_id.
     */
    public vcenterDatastoreObjects: Record<string, IDatastoreInfo>;

    /**
     * List of vcenter hosts.
     */
    public vcenterHostObjects: Array<Item<IHostItem>>;

    public stringService: StringService;

    /**
     * Flag to hide/show AZ Clusters grid.
     */
    public showAzClustersGrid: boolean;

    /**
     * Flag to hide/show AZ Datastores grid.
     */
    public showAzDatastoresGrid: boolean;

    /**
     * Flag to hide/show AZ Hosts grid.
     */
    public showAzHostsGrid: boolean;

    /**
     * Flag to track if fetch AzClusters call is in progress.
     */
    public isFetchAzClustersCallInProgress: boolean;

    /**
     * Flag to track if fetch AzDatastores call is in progress.
     */
    public isFetchAzDatastoresCallInProgress: boolean;

    /**
     * Flag to track if fetch AzHosts call is in progress.
     */
    public isFetchAzHostsCallInProgress: boolean;

    private readonly l10nService: L10nService;

    constructor(args = {}) {
        const extendedArgs = {
            objectName: 'availabilityzone',
            objectType: AvailabilityZone,
            windowElement: 'lazy-load',
            ...args,
        };

        super(extendedArgs);

        this.vcenterClusterObjects = [];
        this.vcenterHostObjects = [];
        this.stringService = this.getNgDependency('stringService');
        this.l10nService = this.getNgDependency('l10nService');
        this.l10nService.registerSourceBundles(dictionary);
    }

    /**
     * Import lazy loaded modal component.
     */
    /* eslint-disable-next-line class-methods-use-this */
    public async getModalComponent(windowElement: TWindowElement): Promise<Type<Component>> {
        const {
            AvailabilityZoneModalComponent,
        } = await import(
            /* webpackChunkName: "availability-zone-modal" */
            // eslint-disable-next-line max-len
            'ng/lazy-loaded-components/modals/availability-zone-modal/availability-zone-modal.component'
        );

        return AvailabilityZoneModalComponent as Type<Component>;
    }

    /** @override */
    protected getModalBreadcrumbTitle(): string {
        return this.l10nService.getMessage(l10nKeys.availabilityZoneLabel);
    }

    /**
     * Fetch vcenter Datastores objects.
     */
    public async fetchVcenterDatastoreObjects(cloudId: string): Promise<void> {
        this.busy = true;
        this.errors = null;
        this.isFetchAzDatastoresCallInProgress = true;
        this.setShowDatastoresGridFlag(false);

        try {
            // There can be only one datastore for Vcenter cloud based Az.
            const azDatastore = this.config.az_datastores.at(0);

            const fetchedDatastores = await azDatastore.fetchVcenterDataStoreList(cloudId);

            if (fetchedDatastores?.length) {
                this.setVcenterDatastoreObjects(fetchedDatastores);
            }

            this.setShowDatastoresGridFlag(true);
        } catch (error) {
            this.errors = error.data;
        } finally {
            this.isFetchAzDatastoresCallInProgress = false;
            this.busy = this.areFetchCallsInProgress;
        }
    }

    /**
     * Fetch NSX Datastores objects.
     */
    public async fetchNsxDatastoreObjects(cloudId: string): Promise<void> {
        const promises: Array<Promise<IVcenterSharedDatastore[]>> = [];

        this.busy = true;
        this.errors = null;
        this.isFetchAzDatastoresCallInProgress = true;
        this.setShowDatastoresGridFlag(false);

        for (const azDatastore of this.config.az_datastores.config) {
            const promise = azDatastore.fetchNsxCloudDataStoreList(
                cloudId,
                this.stringService.slug(azDatastore.config.vcenter_ref),
                true,
            );

            promises.push(promise);
        }

        try {
            const resolvedNsxDatastoresList = await Promise.all(promises);

            resolvedNsxDatastoresList?.forEach(
                (nsxDatastores, index) => {
                    const azDatastore = this.config.az_datastores.at(index);
                    const vcenterId = this.stringService.slug(azDatastore.config.vcenter_ref);

                    this.setNsxDatastoreObjects(vcenterId, nsxDatastores);
                },
            );

            this.setShowDatastoresGridFlag(true);
        } catch (error) {
            this.errors = error.data;
        } finally {
            this.isFetchAzDatastoresCallInProgress = false;
            this.busy = this.areFetchCallsInProgress;
        }
    }

    /**
     * Fetch NSX Host objects.
     */
    public async fetchNsxHostObjects(cloudId: string): Promise<void> {
        const promises: Array<Promise<INsxtTransportnode[]>> = [];

        this.busy = true;
        this.errors = null;
        this.isFetchAzHostsCallInProgress = true;
        this.setShowAzHostsGridFlag(false);

        for (const azHost of this.config.az_hosts.config) {
            const promise = azHost.fetchNsxTransportNodesList(
                this.stringService.slug(azHost.config.vcenter_ref),
                cloudId,
                true,
            );

            promises.push(promise);
        }

        try {
            const resolvedNsxHostsList = await Promise.all(promises);

            resolvedNsxHostsList?.forEach(
                (nsxHosts, index) => {
                    const azHost = this.config.az_hosts.at(index);
                    const vcenterId = this.stringService.slug(azHost.config.vcenter_ref);

                    this.setNsxHostObjects(vcenterId, nsxHosts);
                },
            );

            this.setShowAzHostsGridFlag(true);
        } catch (error) {
            this.errors = error.data;
        } finally {
            this.isFetchAzHostsCallInProgress = false;
            this.busy = this.areFetchCallsInProgress;
        }
    }

    /**
     * Empty the az_clusters repeated message item config.
     */
    public resetAzClusters(): void {
        this.config.az_clusters.removeAll();
    }

    /**
     * Reset Availability Zone DataStore.
     */
    public resetAvailablityZoneDataStore(): void {
        this.config.az_datastores.removeAll();
    }

    /**
     * Reset Availability Zone hosts.
     */
    public resetAzHosts(): void {
        this.config.az_hosts.removeAll();
    }

    /**
     * Add AZ Cluster.
     */
    public addAzCluster(
        isNsxCloud = false,
        cloudRef: string,
        selectedNsxVcenterNames: string[],
        clusterItems: ClusterItem[],
    ): void {
        this.addChildMessageItem({
            field: 'az_clusters',
            modalBindings: {
                isNsxCloud,
                cloudRef,
                selectedNsxVcenterNames,
                clusterItems,
            },
        });
    }

    /**
     * Edit AZ Cluster.
     */
    public editAzCluster(
        azCluster: AZClusterConfigItem,
        isNsxCloud = false,
        cloudRef: string,
        selectedNsxVcenterNames: string[],
        clusterItems: ClusterItem[],
    ): void {
        this.editChildMessageItem({
            field: 'az_clusters',
            messageItem: azCluster,
            modalBindings: {
                isEditing: true,
                isNsxCloud,
                cloudRef,
                selectedNsxVcenterNames,
                clusterItems,
            },
        });
    }

    /**
     * Add AZ Datastore.
     */
    public addAZDatastore(
        cloudRef: string,
        isNsxCloud = false,
        selectedNsxVcenterNames: string[],
    ): void {
        this.addChildMessageItem({
            field: 'az_datastores',
            modalBindings: {
                isNsxCloud,
                cloudRef,
                selectedNsxVcenterNames,
            },
        });
    }

    /**
     * Edt the selected Datastore.
     */
    public editAzDatastore(
        azDatastore: AZDatastoreConfigItem,
        cloudRef: string,
        isNsxCloud = false,
        selectedNsxVcenterNames: string[],
    ): void {
        this.editChildMessageItem({
            field: 'az_datastores',
            messageItem: azDatastore,
            modalBindings: {
                isEditing: true,
                isNsxCloud,
                cloudRef,
                selectedNsxVcenterNames,
            },
        });
    }

    /**
     * Add AZ Host.
     */
    public addAzHost(
        cloudRef: string,
        isNsxCloud = false,
        selectedNsxVcenterNamesForHosts: string[],
        hostItems: Array<Item<IHostItem>>,
    ): void {
        this.addChildMessageItem({
            field: 'az_hosts',
            modalBindings: {
                isNsxCloud,
                cloudRef,
                selectedNsxVcenterNamesForHosts,
                hostItems,
            },
        });
    }

    /**
     * Edit AZ Host.
     */
    public editAzHost(
        azHost: AZHostConfigItem,
        cloudRef: string,
        isNsxCloud = false,
        selectedNsxVcenterNamesForHosts: string[],
        hostItems: Array<Item<IHostItem>>,
    ): void {
        this.editChildMessageItem({
            field: 'az_hosts',
            messageItem: azHost,
            modalBindings: {
                isEditing: true,
                isNsxCloud,
                cloudRef,
                selectedNsxVcenterNamesForHosts,
                hostItems,
            },
        });
    }

    /**
     * Add vSphere Zone.
     */
    public addVsphereZone(cloudRef: string): void {
        this.addChildMessageItem({
            field: 'vsphere_zones',
            modalBindings: {
                cloudRef,
            },
        });
    }

    /**
     * Edit vSphere Zone.
     */
    public editVsphereZone(
        vsphereZone: AZVSphereZoneConfigItem,
        cloudRef: string,
    ): void {
        this.editChildMessageItem({
            field: 'vsphere_zones',
            messageItem: vsphereZone,
            modalBindings: {
                isEditing: true,
                cloudRef,
            },
        });
    }

    /**
     * Fetch NSX Cluster Objects.
     */
    public async fetchNsxClusterObjects(cloudId: string): Promise<void> {
        const promises: Array<Promise<INsxtCluster[]>> = [];

        this.busy = true;
        this.errors = null;
        this.isFetchAzClustersCallInProgress = true;
        this.setShowAzClustersGridFlag(false);

        for (const azCluster of this.config.az_clusters.config) {
            const promise = azCluster.fetchNsxtClustersList(
                cloudId,
                this.stringService.slug(azCluster.config.vcenter_ref),
                true,
            );

            promises.push(promise);
        }

        try {
            const resolvedNsxClustersList = await Promise.all(promises);

            resolvedNsxClustersList?.forEach(
                (nsxClusters, index) => {
                    const azCluster = this.config.az_clusters.at(index);
                    const vcenterId = this.stringService.slug(azCluster.config.vcenter_ref);

                    this.setNsxClusterObjects(vcenterId, nsxClusters);
                },
            );

            this.setShowAzClustersGridFlag(true);
        } catch (error) {
            this.errors = error.data;
        } finally {
            this.isFetchAzClustersCallInProgress = false;
            this.busy = this.areFetchCallsInProgress;
        }
    }

    /**
     * Set fetched NSX Cluster objects.
     */
    public setNsxClusterObjects(vcenterId: string, nsxtClusters: INsxtCluster[]): void {
        if (!this.nsxClusterObjects) {
            this.nsxClusterObjects = {};
        }

        if (!this.nsxClusterObjects[vcenterId]) {
            this.nsxClusterObjects[vcenterId] = {};
        }

        nsxtClusters.forEach(
            nsxtCluster => {
                if (!this.nsxClusterObjects[vcenterId][nsxtCluster.vc_mobj_id]) {
                    this.nsxClusterObjects[vcenterId][nsxtCluster.vc_mobj_id] = nsxtCluster;
                }
            },
        );
    }

    /**
     * Set fetched vCenter Cluster objects.
     */
    public setVcenterAzClusterObjects(vcenterClusterObjects: ClusterItem[]): void {
        this.vcenterClusterObjects = vcenterClusterObjects;
    }

    /**
     * Set show AZ Clusters Grid flag.
     */
    public setShowAzClustersGridFlag(showAzClustersGrid: boolean): void {
        this.showAzClustersGrid = showAzClustersGrid;
    }

    /**
     * Set fetched NSX Datastore objects.
     */
    public setNsxDatastoreObjects(vcenterId: string, nsxDatastores: IVcenterSharedDatastore[]): void {
        if (!this.nsxDatastoreObjects) {
            this.nsxDatastoreObjects = {};
        }

        if (!this.nsxDatastoreObjects[vcenterId]) {
            this.nsxDatastoreObjects[vcenterId] = {};
        }

        nsxDatastores.forEach(
            nsxDatastore => {
                if (!this.nsxDatastoreObjects[vcenterId][nsxDatastore.vc_mobj_id]) {
                    this.nsxDatastoreObjects[vcenterId][nsxDatastore.vc_mobj_id] = nsxDatastore;
                }
            },
        );
    }

    /**
     * Set fetched vcenter Datastore objects.
     */
    public setVcenterDatastoreObjects(vcenterDatastoreObjects: IDatastoreInfo[]): void {
        if (!this.vcenterDatastoreObjects) {
            this.vcenterDatastoreObjects = {};
        }

        vcenterDatastoreObjects.forEach(
            vcenterDatastoreObject => {
                if (!this.vcenterDatastoreObjects[vcenterDatastoreObject.managed_object_id]) {
                    this.vcenterDatastoreObjects[vcenterDatastoreObject.managed_object_id] = vcenterDatastoreObject;
                }
            },
        );
    }

    /**
     * Set fetched NSX Host objects.
     */
    public setNsxHostObjects(vcenterId: string, nsxHosts: INsxtTransportnode[]): void {
        if (!this.nsxHostObjects) {
            this.nsxHostObjects = {};
        }

        if (!this.nsxHostObjects[vcenterId]) {
            this.nsxHostObjects[vcenterId] = {};
        }

        nsxHosts.forEach(
            nsxHost => {
                if (!this.nsxHostObjects[vcenterId][nsxHost.vc_mobj_id]) {
                    this.nsxHostObjects[vcenterId][nsxHost.vc_mobj_id] = nsxHost;
                }
            },
        );
    }

    /**
     * Set fetched vcenter Host objects.
     */
    public setVcenterHostObjects(vcenterHostObjects: Array<Item<IHostItem>>): void {
        this.vcenterHostObjects = vcenterHostObjects;
    }

    /**
     * Set show AZ Clusters Grid flag.
     */
    public setShowDatastoresGridFlag(showAzDatastoresGrid: boolean): void {
        this.showAzDatastoresGrid = showAzDatastoresGrid;
    }

    /**
     * Set show AZ Hosts Grid flag.
     */
    public setShowAzHostsGridFlag(showAzHostsGrid: boolean): void {
        this.showAzHostsGrid = showAzHostsGrid;
    }

    /**
     * Reset vSphere zones config if cloud type is changed to vCenter.
     */
    public resetvSphereZones(): void {
        this.config.vsphere_zones?.removeAll();
    }

    /**
     * Return if any of the fetch clusters, datastores or hosts api call is in progress.
     */
    private get areFetchCallsInProgress(): boolean {
        return this.isFetchAzClustersCallInProgress || this.isFetchAzDatastoresCallInProgress ||
            this.isFetchAzHostsCallInProgress;
    }
}
